import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../common/hooks/hooks";
import {
  toggleCategory,
  replaceVehicles,
  toggleTag,
  toggleBrand,
  setAttributeFilter,
  setPriceRange,
  setOnSale,
  setInStock,
  setSearch,
} from "../../productSlice";
import {
  selectCurrencySymbol,
  selectCategoryTree,
} from "../../productSelectors";
import {
  useGetCategoriesQuery,
  useGetVehiclesQuery,
  useGetTagsQuery,
  useGetBrandsQuery,
  useGetAttributesQuery,
} from "../../api/productTaxonomyApi";
import type { CategoryNode } from "../../types/productTaxonomyApiTypes";
import { findVehiclePath } from "../../helpers/vehicleTreeHelpers";

/**
 * Renders the "Active Filters" section as a series of removable pills.
 * It translates filter IDs and slugs into human-readable labels using the cached taxonomy data.
 */
const ActiveFilters: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t, ready: areTranslationsReady } = useTranslation();

  // Select state from Redux
  const categoryTree = useAppSelector(selectCategoryTree);
  const filters = useAppSelector((state) => state.product.filters);
  const currencySymbol = useAppSelector(selectCurrencySymbol);
  const { data: vehicles = [] } = useGetVehiclesQuery();

  // --- State 1: Data Fetching ---
  // We fetch all taxonomy data required to resolve filter IDs/slugs into human-readable names.
  const {
    data: tagsData,
    isLoading: areTagsLoading,
    isError: isTagsError,
  } = useGetTagsQuery();
  const {
    data: brandsData,
    isLoading: areBrandsLoading,
    isError: isBrandsError,
  } = useGetBrandsQuery();
  const {
    data: attributesData,
    isLoading: areAttributesLoading,
    isError: isAttributesError,
  } = useGetAttributesQuery();
  const { isLoading: areCategoriesLoading, isError: isCategoriesError } =
    useGetCategoriesQuery();

  // --- NEW: Flatten the recursive category tree for fast ID lookup ---
  const categoryLookupMap = useMemo(() => {
    const map: Record<number, CategoryNode> = {};
    const flatten = (nodes: CategoryNode[]) => {
      nodes.forEach((node) => {
        map[node.id] = node;
        if (node.children && node.children.length > 0) {
          flatten(node.children);
        }
      });
    };
    flatten(categoryTree);
    return map;
  }, [categoryTree]);

  // Combine all loading and error states into single flags.
  const isAnyLoading =
    !areTranslationsReady ||
    areCategoriesLoading ||
    areTagsLoading ||
    areBrandsLoading ||
    areAttributesLoading;

  const isAnyError =
    isCategoriesError || isTagsError || isBrandsError || isAttributesError;

  // --- State 2: Explicitly handle loading and error states by rendering nothing ---
  // If any required data is still loading or has failed, we cannot reliably
  // render the filter pills with their correct names.
  if (isAnyLoading || isAnyError) {
    return null;
  }

  // --- State 4 & 5: Success state (with or without data) ---
  const activeFilterPills: React.ReactNode[] = [];

  filters.vehicle.forEach((id) => {
    const path = findVehiclePath(vehicles, id);
    const name = path.map((node) => node.name).join(" / ") || String(id);
    activeFilterPills.push(
      <li key={`vehicle-${id}`} className="active-filters__pill">
        <span>
          {t("productsArchive.vehicle.title", "Uyumlu Araç")}: {name}
        </span>
        <button
          className="active-filters__remove"
          aria-label={t("productsArchive.vehicle.remove", {
            name,
            defaultValue: `${name} araç filtresini kaldır`,
          })}
          onClick={() =>
            dispatch(
              replaceVehicles(filters.vehicle.filter((value) => value !== id)),
            )
          }
        >
          &times;
        </button>
      </li>,
    );
  });

  // 1. Search Filter
  if (filters.search) {
    activeFilterPills.push(
      <li key="search" className="active-filters__pill">
        <span>
          {t("productsArchive.sidebar.searchTitle")}: "{filters.search}"
        </span>
        <button
          onClick={() => dispatch(setSearch(""))}
          aria-label={t("productsArchive.activeFilters.removeSearchLabel", {
            searchTerm: filters.search,
          })}
          className="active-filters__remove"
        >
          &times;
        </button>
      </li>,
    );
  }

  // 2. On Sale Filter
  if (filters.onSale) {
    activeFilterPills.push(
      <li key="on-sale" className="active-filters__pill">
        <span>{t("productsArchive.sidebar.onSaleTitle")}</span>
        <button
          onClick={() => dispatch(setOnSale(false))}
          aria-label={t("productsArchive.activeFilters.removeOnSaleLabel")}
          className="active-filters__remove"
        >
          &times;
        </button>
      </li>,
    );
  }

  // 3. In Stock Filter
  if (filters.inStock) {
    activeFilterPills.push(
      <li key="in-stock" className="active-filters__pill">
        <span>{t("productsArchive.sidebar.inStockTitle", "In Stock")}</span>
        <button
          onClick={() => dispatch(setInStock(false))}
          aria-label={t(
            "productsArchive.activeFilters.removeInStockLabel",
            "Remove In Stock filter",
          )}
          className="active-filters__remove"
        >
          &times;
        </button>
      </li>,
    );
  }

  // 4. Price Filter
  if (filters.minPrice !== null || filters.maxPrice !== null) {
    const min = filters.minPrice ?? 0;
    const maxText =
      filters.maxPrice !== null
        ? ` - ${currencySymbol}${filters.maxPrice}`
        : "+";
    activeFilterPills.push(
      <li key="price" className="active-filters__pill">
        <span>{`${currencySymbol}${min}${maxText}`}</span>
        <button
          onClick={() =>
            dispatch(setPriceRange({ minPrice: null, maxPrice: null }))
          }
          aria-label={t("productsArchive.activeFilters.removePriceLabel")}
          className="active-filters__remove"
        >
          &times;
        </button>
      </li>,
    );
  }

  // 5. Category Filters (Updated to use the lookup map)
  filters.category.forEach((catId) => {
    const category = categoryLookupMap[catId];
    if (category) {
      activeFilterPills.push(
        <li key={`cat-${catId}`} className="active-filters__pill">
          <span>{category.name}</span>
          <button
            onClick={() => dispatch(toggleCategory(catId))}
            aria-label={t("productsArchive.activeFilters.removeCategoryLabel", {
              categoryName: category.name,
            })}
            className="active-filters__remove"
          >
            &times;
          </button>
        </li>,
      );
    }
  });

  // 6. Brand Filters
  if (brandsData) {
    filters.brand.forEach((brandSlug) => {
      const brand = Object.values(brandsData.brands).find(
        (b) => b.slug === brandSlug,
      );
      if (brand) {
        activeFilterPills.push(
          <li key={`brand-${brandSlug}`} className="active-filters__pill">
            <span>{brand.name}</span>
            <button
              onClick={() => dispatch(toggleBrand(brandSlug))}
              aria-label={t("productsArchive.activeFilters.removeBrandLabel", {
                brandName: brand.name,
              })}
              className="active-filters__remove"
            >
              &times;
            </button>
          </li>,
        );
      }
    });
  }

  // 7. Tag Filters
  if (tagsData) {
    filters.tag.forEach((tagId) => {
      const tag = tagsData.tags[tagId];
      if (tag) {
        activeFilterPills.push(
          <li key={`tag-${tagId}`} className="active-filters__pill">
            <span>{tag.name}</span>
            <button
              onClick={() => dispatch(toggleTag(tagId))}
              aria-label={t("productsArchive.activeFilters.removeTagLabel", {
                tagName: tag.name,
              })}
              className="active-filters__remove"
            >
              &times;
            </button>
          </li>,
        );
      }
    });
  }

  // 8. Attribute Filters
  if (attributesData) {
    (Object.entries(filters.attributes) as [string, string[]][]).forEach(
      ([taxonomy, termSlugs]) => {
        const attribute = Object.values(attributesData.attributes).find(
          (a) => a.taxonomy === taxonomy,
        );
        if (attribute) {
          termSlugs.forEach((termSlug) => {
            const term = Object.values(attribute.terms).find(
              (t) => t.slug === termSlug,
            );
            if (term) {
              activeFilterPills.push(
                <li
                  key={`attr-${taxonomy}-${termSlug}`}
                  className="active-filters__pill"
                >
                  <span>{`${attribute.name}: ${term.name}`}</span>
                  <button
                    onClick={() =>
                      dispatch(
                        setAttributeFilter({
                          attribute: taxonomy,
                          term: termSlug,
                        }),
                      )
                    }
                    aria-label={t(
                      "productsArchive.activeFilters.removeAttributeLabel",
                      {
                        attributeName: attribute.name,
                        termName: term.name,
                      },
                    )}
                    className="active-filters__remove"
                  >
                    &times;
                  </button>
                </li>,
              );
            }
          });
        }
      },
    );
  }

  // If no pills were generated, it means no filters are active.
  if (activeFilterPills.length === 0) {
    return null;
  }

  return (
    <div className="active-filters">
      <ul className="active-filters__list">{activeFilterPills}</ul>
    </div>
  );
};

export default ActiveFilters;
